import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetCategory,
  apiPostCategoryItem,
  apiDeleteCategoryItem,
  apiPatchCategoryItem,
} from "@api/category";
import { sortCategoryItems } from "@utils/index";
import { CATEGORY_MAP } from "@constants/index";
import { useMemo } from "react";
import { useUserData } from "./useUserData";
import type {
  CategoryRes,
  CategoryMap,
} from "@/types/category";
import type { UserFavorites } from "@/types/user";

function buildSortedCategories(
  rawData: CategoryRes,
  favorites: UserFavorites
): { data: CategoryRes; map: CategoryMap } {
  const map: CategoryMap = {};
  const data = rawData.map(({ items = [], id, name }, categoryIndex) => {
    const categoryName =
      CATEGORY_MAP[name as keyof typeof CATEGORY_MAP] || name;
    const sortItems = sortCategoryItems(items, favorites, categoryName);
    sortItems.forEach((item, itemIndex) => {
      map[item.id] = [categoryIndex, itemIndex];
    });
    return { id, name: categoryName, items: sortItems };
  });
  return { data, map };
}

export function useCategories() {
  const { data: userData } = useUserData();
  const favorites = userData?.preferences ?? {};

  const {
    data: rawCategories,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: apiGetCategory,
    enabled: !!userData,
  });

  const { data, map } = useMemo(() => {
    if (!rawCategories)
      return { data: [] as CategoryRes, map: {} as CategoryMap };
    return buildSortedCategories(rawCategories, favorites);
  }, [rawCategories, favorites]);

  const isCategoryMap = map && Object.keys(map).length > 0;

  return { data, map, isLoading, error, isCategoryMap };
}

export function useCreateCategoryItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiPostCategoryItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useDeleteCategoryItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiDeleteCategoryItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}

export function useUpdateCategoryItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      apiPatchCategoryItem({ id, value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}
